import { Injectable, computed, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { AuthReadyService } from '@core/services/auth/auth-ready.service';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { SessionService } from '@core/services/auth/session.service';
import { withTransientCallableRetry } from '@core/services/data/callable-transient-retry.util';
import {
  UserAcquisitionSource,
  UserProfessionalContext,
  UserProfile,
  UserPropertyCountStated} from '../../shared/definitions/user';
import {
  User
} from '../../shared/definitions/user';

/**
 * SessionStorage flag flipped on by `markLocallyFinished()` so the
 * `OnboardingCompletedGuard` / `OnboardingAccessGuard` treat the user
 * profile as completed during the brief window between the optimistic
 * navigate and the Firestore listener catching up. Without this, the
 * user gets a noticeable freeze while two callables resolve before
 * landing on the workspace welcome screen — the very thing the user
 * reported as "méga freeze, très mauvaise UX".
 */
const USER_ONBOARDING_JUST_FINISHED_KEY = 'user-onboarding-just-finished';

export function isUserOnboardingJustFinished(): boolean {
  try {
    return sessionStorage.getItem(USER_ONBOARDING_JUST_FINISHED_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearUserOnboardingJustFinished(): void {
  try {
    sessionStorage.removeItem(USER_ONBOARDING_JUST_FINISHED_KEY);
  } catch {
    /* ignore */
  }
}

export const USER_ONBOARDING_STEP_ROUTES: readonly string[] = [
  // The standalone `user-welcome` greeting interstitial was folded into the
  // top of `user-profile` (2026-06-13) — a read-only screen with no input was
  // pure friction before the first real question. `user-welcome` survives only
  // as a redirect (onboarding.routes.ts) so resumed/agency deep links still
  // land cleanly on the profile form.
  'user-profile',
  'user-consent',
] as const;

export const USER_ONBOARDING_STEP_COUNT = USER_ONBOARDING_STEP_ROUTES.length;

export interface UpdateUserProfilePayload {
  firstName: string;
  lastName: string;
  phone?: string;
  professionalContext: UserProfessionalContext;
  propertyCountStated: UserPropertyCountStated;
  acquisitionSource?: UserAcquisitionSource;
  /** "This number is reachable on WhatsApp" (#282). */
  whatsappOptIn?: boolean;
  marketingConsent: boolean;
  locale?: 'en' | 'fr' | 'nl' | 'de';
}

/**
 * Wrapper around the user-level onboarding callables
 * (`updateUserProfile`, `completeUserOnboarding`, `skipUserOnboarding`).
 *
 * The user-onboarding step happens once per human (when the user first
 * lands on the app). Workspace-level onboarding (`OnboardingService`)
 * still runs once per workspace and remains type-specific.
 *
 * Reads come straight from the `AuthStore.user()` signal — the document
 * listener in `AuthService` keeps `users/{uid}.profile.*` in sync.
 */
@Injectable({ providedIn: 'root' })
export class UserOnboardingService {
  private readonly fns = inject(Functions);
  private readonly authStore = inject(AuthStore);
  private readonly auth = inject(AuthService);
  private readonly authReady = inject(AuthReadyService);
  private readonly session = inject(SessionService);

  readonly profile = computed<UserProfile | null>(
    () => this.authStore.user()?.profile ?? null,
  );

  readonly completed = computed<boolean>(
    () => !!this.profile()?.completedAt,
  );

  readonly hasFilledProfile = computed<boolean>(() => {
    const p = this.profile();
    return !!p?.firstName && !!p?.professionalContext;
  });

  /**
   * True when the user signed up via an invitation link and has not yet
   * accepted the invitation. Drives the "you'll join X right after" banner
   * on the user-onboarding screens so the invited user understands they
   * are filling a personal profile, NOT creating a workspace they didn't
   * intend to.
   *
   * SessionStorage is synchronous, so we don't make this a writable signal;
   * the value is stable for the duration of the onboarding session.
   */
  hasPendingInvitation(): boolean {
    return this.session.hasPendingInvitation();
  }

  /**
   * Latest in-flight optimistic profile write. The consent step awaits this
   * (via `whenProfileSaved()`) before calling `completeUserOnboarding`, which
   * the server gates on the profile being present — so optimistic navigation
   * never lets completion race ahead of an unsaved profile.
   */
  private profileWrite: Promise<void> = Promise.resolve();

  /**
   * True once the latest optimistic profile write has resolved successfully.
   * Lets the consent step skip the "Saving your answers…" label when the
   * prefetch kicked off on screen entry already finished (#596).
   */
  private profileSaveDone = false;

  /**
   * The payload of the latest optimistic write, kept so `whenProfileSaved()`
   * can re-issue it. See the comment there for why a memoised promise alone
   * is not enough.
   */
  private lastProfilePayload: UpdateUserProfilePayload | null = null;

  /** Whether the latest optimistic profile write has already landed. */
  isProfileSaveDone(): boolean {
    return this.profileSaveDone;
  }

  /**
   * Start awaiting the in-flight profile write without blocking the UI.
   * The consent step calls this on entry so "Accept and continue" rarely
   * pays for `updateUserProfile` at click time (#596).
   */
  prefetchProfileSave(): void {
    void this.whenProfileSaved();
  }

  async updateProfile(payload: UpdateUserProfilePayload): Promise<void> {
    await this.authReady.whenAuthReady();
    const fn = httpsCallable(this.fns, 'updateUserProfile');
    await fn(payload);
  }

  /**
   * Optimistic profile save: patch the locally cached profile so prefill and
   * the onboarding guards see the answers immediately, fire the callable in
   * the background, and return at once. The caller navigates without awaiting
   * the round-trip. Failures surface at the consent step via `whenProfileSaved`.
   */
  updateProfileOptimistic(payload: UpdateUserProfilePayload): void {
    this.patchLocalProfile(payload);
    this.lastProfilePayload = payload;
    this.profileSaveDone = false;
    const write = this.updateProfile(payload).then(
      () => {
        this.profileSaveDone = true;
      },
      (err: unknown) => {
        this.profileSaveDone = false;
        throw err;
      },
    );
    this.profileWrite = write;
    // Register a no-op handler so a background failure doesn't trip the
    // global unhandled-rejection guard; awaiters of `profileWrite` still
    // observe the rejection.
    write.catch(() => undefined);
  }

  /**
   * Resolves once the latest optimistic profile write has landed. The consent
   * step awaits this before completing onboarding.
   *
   * A rejected promise stays rejected forever, and this one is memoised: with
   * a bare `return this.profileWrite`, the first failure was replayed by every
   * subsequent click on "Accept and continue", so the screen could never
   * recover even once the auth token had arrived (#274). Re-issuing the write
   * from the payload we kept turns the retry the user is already making into a
   * real second attempt.
   *
   * One retry, not a loop: if the re-issued write fails too, the caller gets
   * that rejection and decides what to say.
   */
  async whenProfileSaved(): Promise<void> {
    if (this.profileSaveDone) return;
    try {
      await this.profileWrite;
      this.profileSaveDone = true;
    } catch (err) {
      if (!this.lastProfilePayload) throw err;
      const retry = this.updateProfile(this.lastProfilePayload).then(
        () => {
          this.profileSaveDone = true;
        },
        (retryErr: unknown) => {
          this.profileSaveDone = false;
          throw retryErr;
        },
      );
      this.profileWrite = retry;
      retry.catch(() => undefined);
      await retry;
    }
  }

  /**
   * Wake `completeUserOnboarding` during the consent screen. Sends an
   * intentionally invalid payload (`durationMs: -1`) so Zod rejects before
   * any gate or write — `{}` is a valid empty body and would stamp
   * `completedAt` if a prior partial consent already landed (#597).
   */
  warmupCompleteCallable(): void {
    void this.authReady.whenAuthReady().then(async () => {
      try {
        const fn = httpsCallable(this.fns, 'completeUserOnboarding');
        await fn({ durationMs: -1 });
      } catch {
        // Expected: invalid-argument, or cold-start 401.
      }
    });
  }

  async complete(durationMs?: number): Promise<void> {
    await this.authReady.whenAuthReady();
    await withTransientCallableRetry(async () => {
      const fn = httpsCallable(this.fns, 'completeUserOnboarding');
      await fn(durationMs != null ? { durationMs } : {});
    });
  }

  async skip(reason?: string): Promise<void> {
    await this.authReady.whenAuthReady();
    const fn = httpsCallable(this.fns, 'skipUserOnboarding');
    await fn(reason ? { reason } : {});
  }

  /**
   * Optimistically flip `users/{uid}.profile.completedAt` on the locally
   * cached AuthStore signal so `OnboardingCompletedGuard` /
   * `OnboardingAccessGuard` let the user proceed to the workspace flow
   * immediately. The backend listener overwrites this with the
   * authoritative value once `completeUserOnboarding` returns.
   *
   * Without this the user sees a multi-second freeze while two
   * callables (`updateUserProfile` + `completeUserOnboarding`) resolve
   * sequentially before navigation can proceed.
   */
  markLocallyFinished(payload?: UpdateUserProfilePayload): void {
    try {
      sessionStorage.setItem(USER_ONBOARDING_JUST_FINISHED_KEY, '1');
    } catch {
      /* storage quotas / SSR — safe to ignore */
    }

    const current = this.authStore.user();
    if (!current) return;

    const now = new Date();
    const existingProfile = current.profile;

    // No payload: the consent step completes onboarding without re-sending the
    // profile answers. Just stamp completedAt on the locally cached profile so
    // the guards let the user through; the Firestore listener delivers the
    // authoritative profile right after.
    if (!payload) {
      if (!existingProfile) return;
      this.authStore.setUser(
        new User({
          ...current,
          profile: {
            ...existingProfile,
            completedAt: existingProfile.completedAt ?? now,
            updatedAt: now,
          },
        }),
      );
      return;
    }

    const nextProfile: UserProfile = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      phone: payload.phone,
      professionalContext: payload.professionalContext,
      propertyCountStated: payload.propertyCountStated,
      acquisitionSource: payload.acquisitionSource,
      whatsappOptIn: !!payload.whatsappOptIn,
      marketingConsent: payload.marketingConsent,
      marketingConsentAt: payload.marketingConsent ?
        existingProfile?.marketingConsentAt ?? now :
        existingProfile?.marketingConsentAt,
      locale: payload.locale,
      startedAt: existingProfile?.startedAt ?? now,
      updatedAt: now,
      completedAt: existingProfile?.completedAt ?? now,
      schemaVersion: 1,
    };

    // Patch the User object on the AuthStore. We mirror the same
    // top-level fields the backend writes (firstName/lastName/phone)
    // so any UI bound to the legacy fields refreshes too.
    const next = new User({
      ...current,
      firstName: payload.firstName,
      lastName: payload.lastName,
      phoneNumber: payload.phone ?? current.phoneNumber,
      profile: nextProfile,
    });
    this.authStore.setUser(next);
  }

  /**
   * Patch the locally cached profile with the step-2 (user-profile) answers
   * WITHOUT stamping completion — onboarding is not finished yet here. Used by
   * `updateProfileOptimistic` so the prefill + guards see the answers
   * immediately while the callable persists in the background. Existing
   * `completedAt` and `consents` are preserved (a resume could carry them).
   */
  private patchLocalProfile(payload: UpdateUserProfilePayload): void {
    const current = this.authStore.user();
    if (!current) return;

    const now = new Date();
    const existing = current.profile;
    const nextProfile: UserProfile = {
      firstName: payload.firstName,
      lastName: payload.lastName,
      phone: payload.phone,
      professionalContext: payload.professionalContext,
      propertyCountStated: payload.propertyCountStated,
      acquisitionSource: payload.acquisitionSource,
      whatsappOptIn: !!payload.whatsappOptIn,
      marketingConsent: payload.marketingConsent,
      marketingConsentAt: payload.marketingConsent ?
        existing?.marketingConsentAt ?? now :
        existing?.marketingConsentAt,
      locale: payload.locale,
      startedAt: existing?.startedAt ?? now,
      updatedAt: now,
      completedAt: existing?.completedAt,
      consents: existing?.consents,
      schemaVersion: 1,
    };

    this.authStore.setUser(
      new User({
        ...current,
        firstName: payload.firstName,
        lastName: payload.lastName,
        phoneNumber: payload.phone ?? current.phoneNumber,
        profile: nextProfile,
      }),
    );
  }
}
